<template>
  <div class="wrapper">
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Payslips</h1>
            <p class="text-muted mb-0">
              Payslips are created when you approve a payroll
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add payslip
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Filters -->
    <CCard v-if="items.length" class="mb-4 border">
      <CCardBody class="pb-0">
        <CRow>
          <CCol md="5">
            <CInput
              label="Search"
              v-model="search"
              placeholder="Employee or payslip no."
            />
          </CCol>
          <CCol md="3">
            <CSelect label="Status" :value.sync="statusFilter" :options="statusOptions" />
          </CCol>
          <CCol md="4" class="d-flex align-items-center justify-content-md-end mb-3">
            <span class="text-muted small mr-3">{{ filteredItems.length }} of {{ items.length }} payslips</span>
            <CButton v-if="hasActiveFilters" color="light" size="sm" @click="clearFilters">
              <CIcon name="cil-x" /> Clear filters
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard class="border-0">
      <!-- First load -->
      <CCardBody v-if="loading && !items.length" class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading payslips…</p>
      </CCardBody>

      <!-- Error -->
      <CCardBody v-else-if="loadError && !items.length" class="text-center py-5">
        <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
        <h2 class="h5">Couldn't load payslips</h2>
        <p class="text-muted mb-3">Check your connection and try again.</p>
        <CButton color="primary" @click="refreshTable">
          <CIcon name="cil-reload" /> Try again
        </CButton>
      </CCardBody>

      <!-- Empty -->
      <CCardBody v-else-if="!items.length" class="text-center py-5">
        <CIcon name="cil-file" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No payslips yet</h2>
        <p class="text-muted mb-3">Approve a payroll to create its payslip, or add one by hand.</p>
        <CButton color="primary" @click="addNew">
          <CIcon name="cil-plus" /> Add payslip
        </CButton>
      </CCardBody>

      <!-- Filtered empty -->
      <CCardBody v-else-if="!filteredItems.length" class="text-center py-5">
        <CIcon name="cil-filter" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No payslips match your filters</h2>
        <CButton color="light" class="mt-2" @click="clearFilters">
          <CIcon name="cil-x" /> Clear filters
        </CButton>
      </CCardBody>

      <template v-else>
        <CProgress v-if="loading" :value="100" animated color="primary" size="xs" />
        <!-- Desktop table -->
        <CCardBody class="d-none d-lg-block">
          <CDataTable
            :items="filteredItems"
            :fields="fields"
            items-per-page-select
            :items-per-page="25"
            hover
            sorter
            :sorter-value="{ column: 'date', asc: false }"
            pagination
            responsive
          >
            <template #no="{ item }">
              <td class="py-2 text-monospace">
                <router-link :to="`/tenants/paySlip/${item.id}`">{{ item.no || "—" }}</router-link>
              </td>
            </template>
            <template #date="{ item }">
              <td class="py-2">{{ formatDate(item.date) }}</td>
            </template>
            <template #paySlipTypeDescription="{ item }">
              <td class="py-2">{{ humanize(item.paySlipTypeDescription) }}</td>
            </template>
            <template #totalAmount="{ item }">
              <td class="py-2 text-right font-weight-bold">{{ money(item.totalAmount) }}</td>
            </template>
            <template #statusDescription="{ item }">
              <td class="py-2">
                <CBadge :color="hrStatusColor('payslip', item.statusDescription)">
                  {{ hrStatusLabel('payslip', item.statusDescription) }}
                </CBadge>
              </td>
            </template>
            <template #actions="{ item }">
              <td class="py-2 text-right">
                <CButtonGroup>
                  <CButton color="light" size="sm" class="border" :aria-label="`Open payslip ${item.no || ''}`" title="Open" @click="onEdit(item)">
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton color="light" size="sm" class="border text-danger" :aria-label="`Delete payslip ${item.no || ''}`" title="Delete" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" />
                  </CButton>
                </CButtonGroup>
              </td>
            </template>
          </CDataTable>
        </CCardBody>

        <!-- Mobile cards -->
        <CCardBody class="d-lg-none">
          <CCard v-for="item in filteredItems" :key="item.id" class="mb-3 border">
            <CCardBody class="py-3">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <router-link :to="`/tenants/paySlip/${item.id}`" class="font-weight-bold">{{ item.profileName }}</router-link>
                  <small class="d-block text-muted">
                    <span class="text-monospace">{{ item.no }}</span> · {{ formatDate(item.date) }}
                  </small>
                </div>
                <CBadge :color="hrStatusColor('payslip', item.statusDescription)">
                  {{ hrStatusLabel('payslip', item.statusDescription) }}
                </CBadge>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <div class="text-muted small">Net pay</div>
                  <div class="font-weight-bold">{{ money(item.totalAmount) }}</div>
                </div>
                <CDropdown color="light" placement="bottom-end">
                  <template #toggler-content>
                    <CIcon name="cil-options" />
                    <span class="sr-only">More actions</span>
                  </template>
                  <CDropdownItem @click="onEdit(item)">
                    <CIcon name="cil-pencil" class="mr-2" /> Open
                  </CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" class="mr-2" /> Delete
                  </CDropdownItem>
                </CDropdown>
              </div>
            </CCardBody>
          </CCard>
        </CCardBody>
      </template>
    </CCard>

    <CModal
      :title="`Delete payslip ${itemToDelete.no || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The payslip for <strong>{{ itemToDelete.profileName }}</strong>
        dated {{ formatDate(itemToDelete.date) }} (net pay {{ money(itemToDelete.totalAmount) }})
        will be removed. The employee won't see it any more. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" /> Delete payslip
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PaySlipApi from "@/lib/paySlipApi";
import { formatMyr } from "@/utils/money";
import { formatDate } from "@/utils/datetime";
import { hrStatusMixin, hrStatusOptions } from "@/utils/hrStatusBadges";

const fields = [
  { key: "no", label: "Payslip no." },
  { key: "date", label: "Date" },
  { key: "profileName", label: "Employee" },
  { key: "paySlipTypeDescription", label: "Type" },
  { key: "totalAmount", label: "Net pay", _style: "text-align:right" },
  { key: "statusDescription", label: "Status" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "PaySlipList",
  mixins: [hrStatusMixin],
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      fields,
      api: new PaySlipApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
      search: "",
      statusFilter: "",
      statusOptions: hrStatusOptions("payslip"),
    };
  },
  mounted() {
    // EmployeeList links here with ?search=<employee name>
    if (this.$route.query.search) this.search = String(this.$route.query.search);
    this.refreshTable();
  },
  computed: {
    rows() {
      return this.items.map((item) => ({
        ...item,
        profileName: (item.profile && item.profile.fullName) || item.employeeName || "",
      }));
    },
    hasActiveFilters() {
      return !!(this.search || this.statusFilter);
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      return this.rows.filter((item) => {
        if (this.statusFilter && item.statusDescription !== this.statusFilter) return false;
        if (!q) return true;
        return (
          (item.profileName || "").toLowerCase().includes(q) ||
          (item.no || "").toLowerCase().includes(q)
        );
      });
    },
  },
  methods: {
    formatDate,
    money(v) {
      return `RM ${formatMyr(v)}`;
    },
    humanize(name) {
      if (!name) return "";
      const words = String(name).replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
      return words.charAt(0).toUpperCase() + words.slice(1);
    },
    clearFilters() {
      this.search = "";
      this.statusFilter = "";
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error("Couldn't load payslips", err);
          this.loadError = true;
          if (this.items.length) {
            this.$toast.error("Couldn't refresh payslips. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/PaySlip/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Payslip deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't delete the payslip. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/PaySlip" });
    },
  },
};
</script>
